/**
 * "Now open worldwide" landing-page section. Renders nothing while the
 * us_only signup policy is on — it auto-appears the day the admin flips
 * the Worldwide Buyers toggle, turning the launch into a visible moment.
 * Shows aggregate country counts only (no PII).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { GlobeHemisphereWestIcon as GlobeHemisphereWest, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const NAME = (code) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

export default function GlobalReachSection() {
  const [reach, setReach] = useState(null);

  useEffect(() => {
    api.get("/public/global-reach")
      .then((r) => { if (r.data?.enabled) setReach(r.data); })
      .catch(() => {});
  }, []);

  if (!reach) return null;

  return (
    <section className="bg-ink text-white py-16 md:py-20" data-testid="global-reach-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
          <GlobeHemisphereWest size={14} weight="duotone" /> Global registry
        </p>
        <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1] max-w-2xl">
          Now open worldwide.
        </h2>
        <p className="text-sm text-[#C4C4C4] mt-3 max-w-xl leading-relaxed">
          Register your dogs, build pedigrees, and connect with breeders from
          anywhere{reach.total_countries > 1 ? ` — already joined from ${reach.total_countries} ${reach.total_countries === 1 ? "country" : "countries"}` : ""}.
          Pricing in USD; seller payouts currently US-only.
        </p>
        {reach.countries?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {reach.countries.slice(0, 18).map((c) => (
              <span
                key={c.country}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-[#E8E8E8]"
                data-testid={`reach-country-${c.country}`}
              >
                {NAME(c.country)}
                <span className="text-gold font-bold ml-1.5">{c.count}</span>
              </span>
            ))}
          </div>
        )}
        <Link
          to="/register"
          className="inline-flex items-center gap-1.5 mt-8 rounded-full bg-gold hover:bg-white text-ink px-6 py-3 text-sm font-bold transition-colors"
          data-testid="global-reach-cta"
        >
          Join from anywhere <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </section>
  );
}
